import { Box, Paper, Slider, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isoDateToUtcTimestamp(isoDate) {
  return new Date(`${isoDate}T00:00:00Z`).getTime();
}

function differenceInDays(startIsoDate, endIsoDate) {
  return Math.round(
    (isoDateToUtcTimestamp(endIsoDate) - isoDateToUtcTimestamp(startIsoDate)) /
      MS_PER_DAY
  );
}

function addDaysToIsoDate(isoDate, days) {
  const nextDate = new Date(`${isoDate}T00:00:00Z`);
  nextDate.setUTCDate(nextDate.getUTCDate() + days);
  return nextDate.toISOString().slice(0, 10);
}

function formatTimelineDate(isoDate) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));
}

export default function UpdatedTimeline({
  minDate,
  maxDate,
  selectedFrom,
  selectedTo,
  onCommit,
}) {
  const maxOffset = useMemo(
    () => differenceInDays(minDate, maxDate),
    [minDate, maxDate]
  );

  const [draftRange, setDraftRange] = useState([0, maxOffset]);

  useEffect(() => {
    setDraftRange([
      differenceInDays(minDate, selectedFrom),
      differenceInDays(minDate, selectedTo),
    ]);
  }, [maxOffset, minDate, selectedFrom, selectedTo]);

  const marks = useMemo(
    () => [
      { value: 0 },
      { value: maxOffset },
    ],
    [maxOffset]
  );

  const handleCommittedChange = (_, nextRange) => {
    const [startOffset, endOffset] = nextRange;
    onCommit(
      addDaysToIsoDate(minDate, startOffset),
      addDaysToIsoDate(minDate, endOffset)
    );
  };

  return (
    <Paper
      sx={{
        mb: 0.5,
        p: 2,
        borderRadius: 3,
        backgroundColor: "#ffffff",
        border: "1px solid rgba(15, 23, 42, 0.06)",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", color: "#0f172a" }}>
        Updated Window
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: "0.84rem", color: "#64748b" }}>
        Drag the timeline to choose the visible update range.
      </Typography>

      <Box sx={{ mt: 1.75, px: { xs: 0.5, md: 1 } }}>
        <Slider
          value={draftRange}
          min={0}
          max={maxOffset}
          step={1}
          marks={marks}
          disableSwap
          valueLabelDisplay="auto"
          valueLabelFormat={(value) =>
            formatTimelineDate(addDaysToIsoDate(minDate, value))
          }
          onChange={(_, nextRange) => setDraftRange(nextRange)}
          onChangeCommitted={handleCommittedChange}
        />
        <Box
          sx={{
            mt: 0.25,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 0.25, md: 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.78rem", md: "0.82rem" },
              color: "#64748b",
              whiteSpace: "nowrap",
            }}
          >
            {formatTimelineDate(minDate)}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "0.78rem", md: "0.82rem" },
              color: "#64748b",
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
          >
            {formatTimelineDate(maxDate)}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 1.25,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: "0.88rem", color: "#334155" }}>
          <strong>From:</strong> {formatTimelineDate(selectedFrom)}
        </Typography>
        <Typography sx={{ fontSize: "0.88rem", color: "#334155" }}>
          <strong>To:</strong> {formatTimelineDate(selectedTo)}
        </Typography>
      </Box>
    </Paper>
  );
}
